/**
 * StatsRecovery Component
 *
 * This component provides a UI for users to restore their stats from backups.
 * It should be imported and used in the account page (hesabim).
 *
 * Example usage in hesabim page:
 *
 * ```tsx
 * import StatsRecovery from '@components/Home/StatsRecovery'
 *
 * // Inside your account page component:
 * {activeTab === 'stats' && <StatsRecovery />}
 * ```
 */

import { useUserData } from '@contexts/UserDataContext'
import {
  formatBackupDate,
  getAllBackups,
  restoreFromBackup,
} from 'helpers/indexedDbBackup'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { FiBarChart2, FiCalendar, FiClock, FiDatabase, FiRefreshCw } from 'react-icons/fi'

export type BackupEntry = {
  id: string
  timestamp: number
  stats: Record<string, any>
}

export const StatsRecovery = () => {
  const { updateUserData } = useUserData()
  const [showBackups, setShowBackups] = useState(false)
  const [backups, setBackups] = useState<BackupEntry[]>([])
  const [loading, setLoading] = useState(true)

  // Load backups on mount
  useEffect(() => {
    const loadBackups = async () => {
      try {
        setLoading(true)
        const backupData = await getAllBackups()
        setBackups(backupData)
      } catch (error) {
        console.error('Failed to load backups:', error)
      } finally {
        setLoading(false)
      }
    }

    loadBackups()
  }, [])

  const hasBackups = backups.length > 0

  if (loading) {
    return (
      <div className="py-6 rounded-xl bg-surface-subtle border border-main">
        <div className="flex items-center">
          <FiClock className="w-5 h-5 mr-3 text-brand-secondary animate-spin" />
          <h3 className="text-lg font-medium text-main">Yedekler Yükleniyor...</h3>
        </div>
      </div>
    )
  }

  if (!hasBackups) {
    return (
      <div className="p-6 rounded-xl bg-surface-subtle border border-main">
        <div className="flex items-center">
          <FiDatabase className="w-5 h-5 mr-3 text-muted" />
          <h3 className="text-lg font-medium text-main">İstatistik Yedekleri</h3>
        </div>
        <p className="mt-3 text-sm text-muted">
          Henüz hiç istatistik yedeği oluşturulmamış. İstatistikleriniz kaydedildikçe
          otomatik olarak yedeklenecektir.
        </p>
      </div>
    )
  }

  const handleRestore = async (index = 0) => {
    if (index >= backups.length) {
      toast.error('Yedek bulunamadı.', { duration: 3000 })
      return
    }

    try {
      const restoredStats = await restoreFromBackup(index)

      if (restoredStats) {
        updateUserData('stats', restoredStats)
        toast.success('İstatistikleriniz başarıyla geri yüklendi.', {
          duration: 3000,
          icon: '✅',
        })
      } else {
        throw new Error('Backup restoration failed')
      }
    } catch (error) {
      console.error('Failed to restore backup:', error)
      toast.error('İstatistikleri geri yüklerken bir hata oluştu.', {
        duration: 3000,
      })
    }
  }

  return (
    <div className="p-6 mb-8 bg-main rounded-2xl border border-main sacred-shadow">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center">
          <div className="p-2 rounded-lg bg-info-subtle mr-3">
            <FiBarChart2 className="w-5 h-5 text-info" />
          </div>
          <h3 className="text-xl font-semibold text-main">İstatistik Kurtarma</h3>
        </div>
      </div>

      <p className="mb-6 text-sm text-muted leading-relaxed">
        İstatistikleriniz uygulamayı kullandıkça otomatik olarak IndexedDB veritabanında
        yedeklenir. Eğer istatistiklerinizin kaybolduğunu düşünüyorsanız, aşağıdaki
        yedeklerden birini geri yükleyebilirsiniz.
      </p>

      <div className="mb-4">
        <button
          className="w-full px-6 py-3 text-base font-semibold text-white bg-brand-secondary rounded-xl hover:bg-brand-secondary/90 focus:outline-hidden focus:ring-4 focus:ring-brand-secondary/20 transition-all active:scale-[0.99]"
          onClick={() => handleRestore(0)}
        >
          <div className="flex items-center justify-center">
            <FiRefreshCw className="w-5 h-5 mr-2.5" />
            En Son Yedekten Geri Yükle
          </div>
        </button>
      </div>

      <div className="mb-2">
        <button
          aria-label="Tüm Yedekleri Göster/Gizle"
          className="flex items-center justify-between w-full px-4 py-2 text-sm font-medium text-main bg-surface-subtle rounded-lg hover:bg-surface-strong border border-main transition-colors"
          onClick={() => setShowBackups(!showBackups)}
        >
          <span>Tüm Yedekler ({backups.length})</span>
          <span className="opacity-60">{showBackups ? '▲' : '▼'}</span>
        </button>
      </div>

      {showBackups && (
        <div className="mt-3 overflow-hidden border border-main rounded-xl bg-surface-subtle/50">
          <ul className="divide-y divide-main">
            {backups.map((backup, index) => (
              <li
                key={backup.timestamp}
                className="flex items-center justify-between p-4 hover:bg-surface-strong transition-colors"
              >
                <div>
                  <div className="flex items-center text-sm font-semibold text-main">
                    <FiCalendar className="w-4 h-4 mr-2 text-muted" />
                    {formatBackupDate(backup.timestamp)}
                  </div>
                  <div className="mt-1 text-xs text-muted">
                    {Object.keys(backup.stats).length} günlük kayıt
                  </div>
                </div>
                <button
                  aria-label={`${formatBackupDate(backup.timestamp)} tarihli yedeği geri yükle`}
                  onClick={() => handleRestore(index)}
                  className="px-4 py-1.5 text-xs font-bold text-brand-secondary bg-brand-secondary-subtle rounded-full hover:bg-brand-secondary/15 hover:text-emerald-900 dark:hover:bg-brand-secondary dark:hover:text-white transition-all active:scale-[0.95]"
                >
                  Geri Yükle
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

export default StatsRecovery
